import { useState } from "react";

const LAYERS = [
  {
    id: "kdf",
    label: "Key Derivation",
    badge: "bg-violet-500/20 text-violet-300 border-violet-500/40",
    acc: "text-violet-300", dot: "bg-violet-400",
    items: [
      {
        name: "Argon2id",
        role: "Key derivation function",
        detail: "Memory-hard KDF with 64 MB memory cost and 3 passes. Derives the vault key from the master password. Resistant to GPU and ASIC brute-force attacks. RFC 9106 compliant.",
        params: ["Memory: 64 MB", "Iterations: 3 passes", "Parallelism: 4 lanes", "Output: 32-byte key"],
      },
    ],
  },
  {
    id: "encryption",
    label: "Credential Encryption",
    badge: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    acc: "text-cyan-300", dot: "bg-cyan-400",
    items: [
      {
        name: "AES-256-GCM",
        role: "Per-entry authenticated encryption",
        detail: "Every credential is encrypted individually before being written to SQLite. A new 96-bit random nonce is generated per entry — reusing nonces with GCM is catastrophic, so uniqueness is enforced by the crypto layer.",
        params: ["Key size: 256-bit", "Nonce: 96-bit random per entry", "Auth tag: 128-bit", "Storage: ciphertext + nonce + tag in SQLite"],
      },
    ],
  },
  {
    id: "dpapi",
    label: "Windows DPAPI Binding",
    badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    acc: "text-emerald-300", dot: "bg-emerald-400",
    items: [
      {
        name: "CryptProtectData",
        role: "Windows account key wrapping",
        detail: "The vault key is optionally wrapped with Windows DPAPI (CryptProtectData) tied to the current Windows SID. On relaunch, CryptUnprotectData silently recovers the vault key for the registered account — no master password prompt. Any other Windows user must use the master password path.",
        params: ["Binding: Windows SID", "Scope: current user", "Fallback: master password", "API: CryptProtectData / CryptUnprotectData"],
      },
    ],
  },
  {
    id: "memory",
    label: "In-Memory Key Management",
    badge: "bg-red-500/20 text-red-300 border-red-500/40",
    acc: "text-red-300", dot: "bg-red-400",
    items: [
      {
        name: "Zeroed bytearray",
        role: "In-memory key with zero-on-lock",
        detail: "The vault key lives in a Python bytearray (mutable, not an immutable bytes object) so it can be explicitly overwritten with zeros on lock. Immutable objects cannot be zeroed — this design choice prevents the key from persisting in memory after lock.",
        params: ["Type: mutable bytearray", "Cleared: immediately on lock", "Never written to disk in plaintext", "Clipboard: auto-cleared after 30 seconds"],
      },
    ],
  },
];

const PRIMITIVES = [
  { name: "AES-256-GCM", use: "Credential encryption", strength: "256-bit authenticated" },
  { name: "Argon2id",    use: "Key derivation",         strength: "Memory-hard, RFC 9106" },
  { name: "DPAPI",       use: "Key wrapping",            strength: "Windows account-bound" },
  { name: "96-bit nonce",use: "GCM IV per entry",        strength: "Random, unique per write" },
  { name: "128-bit tag", use: "GCM auth tag",            strength: "Tamper detection" },
  { name: "bytearray",   use: "In-memory key store",     strength: "Mutable — zero-on-lock" },
];

export default function EncryptionEngine() {
  const [open, setOpen] = useState("encryption");

  return (
    <div className="space-y-4">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">ENCRYPTION ENGINE</p>
        <h4 className="mt-1 text-sm font-semibold text-white">Cryptographic stack</h4>
        <p className="mt-1 text-xs text-slate-400">
          Four independent cryptographic layers work together: key derivation, per-entry encryption, OS-level key wrapping, and secure in-memory key management with explicit zeroing on lock.
        </p>
      </div>

      {/* Layer cards */}
      <div className="space-y-2">
        {LAYERS.map((layer) => (
          <div key={layer.id} className="rounded-xl border border-white/10 bg-white/5">
            <button
              onClick={() => setOpen(open === layer.id ? null : layer.id)}
              className="flex w-full items-center gap-3 p-3 text-left"
              aria-expanded={open === layer.id}
            >
              <span className={`h-2 w-2 shrink-0 rounded-full ${layer.dot}`} />
              <span className={`font-mono text-[10px] tracking-[0.15em] ${layer.acc}`}>{layer.label.toUpperCase()}</span>
              <svg className={`ml-auto h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${open === layer.id ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {open === layer.id && (
              <div className="border-t border-white/10 px-3 pb-3 pt-2 space-y-2">
                {layer.items.map((item) => (
                  <div key={item.name} className="rounded-lg border border-white/10 bg-black/20 p-3">
                    <p className="text-sm font-semibold text-white">{item.name}</p>
                    <p className={`text-[10px] ${layer.acc}`}>{item.role}</p>
                    <p className="mt-1.5 text-[11px] leading-relaxed text-slate-400">{item.detail}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {item.params.map((p) => (
                        <span key={p} className={`rounded border px-2 py-0.5 font-mono text-[9px] ${layer.badge}`}>{p}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Primitives summary */}
      <div className="rounded-xl border border-white/10 bg-black/20 p-3">
        <p className="mb-2 font-mono text-[9px] tracking-[0.15em] text-slate-500">CRYPTOGRAPHIC PRIMITIVES</p>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {PRIMITIVES.map(({ name, use, strength }) => (
            <div key={name} className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2">
              <span className="mt-0.5 font-mono text-[10px] font-bold text-violet-300 min-w-[90px]">{name}</span>
              <div>
                <p className="text-[10px] text-slate-300">{use}</p>
                <p className="text-[9px] text-slate-500">{strength}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
